import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { t } from 'i18next';
import { createRef, useEffect, useRef, useState } from 'react';
import { RuleCategory, getRuleCategoryLabel } from '../../../../common/enums/RuleCategory';
import { Ruleset } from '../../../../common/enums/Ruleset';
import { useTabbable } from '../../../../common/hooks/useTabbable';
import { RuleSummary } from '../../common/components/RuleSummary';
import { RuleToggle } from '../../common/components/RuleToggle';
import { CATEGORY_ACCORDION_SX } from '../../common/styles/accordion';
import { FUZZER_CONSTANTS } from '../../common/styles/constants';
import { PROFILE_ROOT_STYLES } from '../../common/styles/root';
import { FuzzerRule } from '../../models/FuzzerRule';
import { FuzzerStore } from '../../store/FuzzerStore';

type FuzzerTabProps = {
  hidden: boolean;
  ruleset: Ruleset;
  ruleGroups: Map<RuleCategory, FuzzerRule[]>;
};

type FuzzerGroupProps = {
  category: RuleCategory;
  ruleset: Ruleset;
  rules: FuzzerRule[];
};

type FuzzerEntryProps = {
  rule: FuzzerRule;
};

const FuzzerRuleEntry = ({ rule }: FuzzerEntryProps) => {
  const store = useRef(FuzzerStore.getInstance()).current;
  const [boxRef, onKeyDown] = useTabbable();

  return (
    <Stack role="listitem" ref={boxRef} onKeyDown={onKeyDown} className="profile-row border-bottom">
      <RuleSummary number={rule.getNumber()} title={rule.getTitle()} subtitle={rule.getShortExplanation()} />
      <RuleToggle ruleNumber={rule.getNumber()} store={store} />
    </Stack>
  );
};

const FuzzerGroupEntry = ({ category, ruleset, rules }: FuzzerGroupProps) => {
  return (
    <Accordion disableGutters defaultExpanded>
      <AccordionSummary
        id={`rule-group-category-${category}-${ruleset}-header`}
        aria-controls={`rule-group-category-${category}-${ruleset}-content`}
        expandIcon={<ExpandMore />}
        title={t('a11y.profile.fuzzerCategoryAccordion', { category: getRuleCategoryLabel(category), ruleset })}
        className="border-bottom"
      >
        {getRuleCategoryLabel(category)}
      </AccordionSummary>
      <AccordionDetails role="list">
        {rules.map((rule) => (
          <FuzzerRuleEntry key={rule.getNumber()} rule={rule} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const FuzzerTab = ({ hidden, ruleset, ruleGroups }: FuzzerTabProps) => {
  return (
    <Box hidden={hidden} aria-hidden={hidden} data-role={`fuzzer-tab-${ruleset}`}>
      {Array.from(ruleGroups.entries()).map(([category, rules]) => (
        <FuzzerGroupEntry key={category} category={category} ruleset={rules[0].getRuleset()} rules={rules} />
      ))}
    </Box>
  );
};

export const FuzzerTable = () => {
  const [tab, setTab] = useState(0);
  const tabRef = createRef<HTMLDivElement>();
  const headingRef = createRef<HTMLDivElement>();

  const { HEADER_HEIGHT, TABS_HEIGHT, ACCORDION_STICKY_TOP, RULE_STICKY_TOP } = FUZZER_CONSTANTS;

  const store = useRef(FuzzerStore.getInstance()).current;

  useEffect(() => {
    if (!tabRef.current || !headingRef.current) return;

    // On ruleset tab change, if the new tab is scrolled below the heading, reset
    // its scroll to display the rules from the first one
    const { top } = tabRef.current.getBoundingClientRect();
    const { bottom } = headingRef.current.getBoundingClientRect();
    if (top < bottom) {
      tabRef.current.scrollIntoView({ block: 'start', inline: 'start' });
    }
  }, [tab, headingRef, tabRef]);

  return (
    <Box
      component={Paper}
      data-role="fuzzer-table"
      sx={deepmerge(PROFILE_ROOT_STYLES, {
        ...CATEGORY_ACCORDION_SX({ stickyOffset: ACCORDION_STICKY_TOP }),
        '& .profile-rule-summary': { scrollMarginTop: RULE_STICKY_TOP },
      })}
    >
      <Box ref={headingRef} sx={{ position: 'sticky', top: 0, zIndex: 3 }}>
        <Tabs className="border-bottom" value={tab} onChange={(_, value: number) => setTab(value)} sx={{ height: TABS_HEIGHT, backgroundColor: 'white' }}>
          <Tab label={t('fuzzerRuleset.compliance')} />
          <Tab label={t('fuzzerRuleset.vulnerability')} />
        </Tabs>
        <Stack className="profile-header" sx={{ height: HEADER_HEIGHT, justifyContent: 'space-between' }}>
          <Typography>{t('rule')}</Typography>
          <Typography>{t('status')}</Typography>
        </Stack>
      </Box>
      <Box ref={tabRef} sx={{ scrollMarginTop: ACCORDION_STICKY_TOP }}>
        <FuzzerTab hidden={tab === 1} ruleset={Ruleset.COMPLIANCE} ruleGroups={store.complianceRules.value} />
        <FuzzerTab hidden={tab === 0} ruleset={Ruleset.VULNERABILITY} ruleGroups={store.vulnerabilityRules.value} />
      </Box>
    </Box>
  );
};
